import React, {
	createContext,
	FC,
	ReactNode,
	useEffect,
	useMemo,
	useState,
	useCallback,
	useContext,
	useLayoutEffect,
	useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
	openDatabase,
	storeUsers,
	getAllUsers,
	storeCurrencyPolicies,
	removeAllUsers,
	removeCurrencyPolicies,
	getAllCurrencyPolicies,
} from './indexdbConfig';
import {
	ClientVisibilityPermissions,
	isClientOrOfficeUserType,
	maskClientUser,
} from '../helpers/clientVisibility';
import AuthContext from './authContext';
import { useLiveReflections } from './liveReflectionContext';
import { LiveReflectionEvent, LiveReflectionToken } from '../liveReflections';
import { getUsersDetailsReportUsers } from '../helpers/usersDetailsReportUsers';
import { getCurrencies } from '../apis';


interface IPublicSettingsProps {
	"id": number;
	"key": string;
	"value": boolean | string | number;
}


interface Video {
	_id: number;
	title: string;
	url: string;
	thumbnail: string;
	type: string;
	description: string;
}



export interface IEducationCenterProps {
	playlists?: Video;
}

export interface IUserProps {
	accountID?: number;
	accountId?: number;
	accountIdPrefix?: string;
	closeOnly?: boolean;
	creditLoanPercentage?: number;
	currencyPolicyId?: number;
	currencySign?: string;
	demo?: boolean;
	enableCashDelivery?: boolean;
	firstName?: string;
	firstname?: string;
	genericPolicyId?: number;
	id?: number;
	isDemo?: boolean;
	lastName?: string;
	lastname?: string;
	locked?: boolean;
	mobile?: string;
	mobileNumber?: string;
	
	parentId?: number;
	percentageLevel1?: number;
	percentageLevel2?: number;
	percentageLevel3?: number;
	percentageLevel4?: number;
	tradingType?: number;
	userCurrencyId?: number;
	userID?: number;
	userName?: string;
	username?: string;
	userType?: number;
	userTypeId?: number;
	whiteLabel?: boolean;
	gmtOffset?: string;
	serverGmtOffset?: string;
	status?: string;
	statusNote?: string;
	source?: string;
	lastActivityDate?: string;
	lastTreeUpdateDate?: string;
	lastCallDate?: string;
	lastStatusUpdateDate?: string;
	lastStatusUpdatedById?: number | string | null;
}

const normalizeConfigUser = (user: any): Partial<IUserProps> => ({
	...user,
	id: user.id ?? user.userID,
	userID: user.userID ?? user.id,
	accountId: user.accountId ?? user.accountID,
	currencyPolicyId: user.currencyPolicyId ?? user.currenciesPoliciesID,
	demo: user.demo ?? user.isDemo,
	genericPolicyId: user.genericPolicyId ?? user.genericPoliciesID,
	userName: user.userName ?? user.username,
});

type UserVisibilityInput =
	| boolean
	| Boolean
	| ClientVisibilityPermissions
	| null
	| undefined;

type UserCacheScope = {
	id?: Number | number | string;
	token?: string;
	clientTreeUserIdNode?: Array<Number | number | string>;
	dealerTreeUserIdNode?: Array<Number | number | string>;
};

const hashCacheScope = (value: string) => {
	let hash = 2166136261;
	for (let index = 0; index < value.length; index += 1) {
		hash ^= value.charCodeAt(index);
		hash = Math.imul(hash, 16777619);
	}
	return (hash >>> 0).toString(36);
};

const getUserCacheOwner = (user: UserCacheScope) => {
	const normalizedUserId = Number(user.id);
	if (!Number.isFinite(normalizedUserId) || normalizedUserId <= 0) {
		return 'anonymous';
	}

	const visibleTreeIds = [
		normalizedUserId,
		...(user.clientTreeUserIdNode || []).map(Number),
		...(user.dealerTreeUserIdNode || []).map(Number),
	]
		.filter((id) => Number.isFinite(id) && id > 0)
		.filter((id, index, ids) => ids.indexOf(id) === index)
		.sort((left, right) => left - right);

	return `${normalizedUserId}|tree:${hashCacheScope(visibleTreeIds.join(','))}`;
};

export const getUserVisibilityFingerprint = (
	permissions: ClientVisibilityPermissions,
	owner = 'anonymous',
) =>
	`owner:${owner}|names:${permissions.canSeeClientNames === true ? 1 : 0}|mobile:${
		permissions.canSeeMobileNumbers === true ? 1 : 0
	}`;

const buildUserLookup = (sourceUsers: Partial<IUserProps>[]) => {
	const userMap = new Map<number, Partial<IUserProps>>();
	for (const user of sourceUsers) {
		const ids = [Number(user?.id), Number(user?.userID)].filter((id) => Number.isFinite(id));
		ids.forEach((id) => userMap.set(id, user));
	}
	return userMap;
};

interface ICurrencyPolicyProps {
	requiredMarginTypeId?: number;
	openCommissionTypeId?: number;
	closedCommissionTypeId?: number;
	intraDayCommissionValue?: number;
	requiredMargin?: number;
	sellRequiredMarginType?: number;
	sellRequiredMargin?: number;
	buyHoldingMarginType?: number;
	buyHoldingMargin?: number;
	sellHoldingMarginType?: number;
	sellHoldingMargin?: number;
	spreadBalance?: number;
	spread?: number;
	entryAutoBroker?: number;
	limitOffset?: number;
	marketAutoBroker?: number;
	maximumAmountPerDeal?: number;
	maximumQuantity?: number;
	maximumValue?: number;
	percentage?: number;
	stopOffset?: number;
	intraDayCommissionTypeId?: number;
	sellOnly?: boolean;
	buyOnly?: boolean;
	limitOffsetTypeId?: number;
	stopOffsetTypeId?: number;
	minimumAmountPerDeal?: number;
	currencyId?: number;
	closedCommissionValue?: number;
	openCommissionValue?: number;
	visible?: boolean;
	spreadTypeId?: number;
}
export interface ICurrencyProps {
	amountUnitId?: number;
	autoCancelEntryOrders?: boolean;
	autoCancelSltpOrders?: boolean;
	autoStopTrade?: boolean;
	autoStopTradeSeconds?: number;
	buyOnly?: boolean;
	closeCancelAllOnExpiry?: boolean;
	closeDay?: number;
	closeTime?: string;
	closed?: boolean;
	contractSize?: number;
	currencyTypeId?: number;
	dailyCloseTimeFrom1?: string;
	dailyCloseTimeFrom2?: string;
	dailyCloseTimeFrom3?: string;
	dailyCloseTimeTo1?: string;
	dailyCloseTimeTo2?: string;
	dailyCloseTimeTo3?: string;
	decimalDigits?: number;
	description?: string;
	directCalculation?: boolean;
	expiryDate?: string;
	feederName?: string;
	id?: number;
	moveIfClosed?: boolean;
	name?: string;
	openDay?: number;
	openTime?: string;
	parentId?: number;
	refDirectCalculation?: boolean;
	referenceCurrencyId?: number;
	requotable?: boolean;
	rowColor?: string;
	sellOnly?: boolean;
	spreadFromBid?: boolean;
	tickDigits?: number;
	used?: boolean;
}

export interface IBanksProps {
	details: string;
	bankImageUrl: string;
	name: string;
	id: number;
}

export interface IConfigContextProps {
	usersHMap: any;
	publicSettings: Partial<IPublicSettingsProps>;
	setPublicSettingsData(...args: unknown[]): unknown;
	users: any;
	lastUsersFetchedAt: string | null;
	setUsersData(...args: unknown[]): unknown;
	refreshUsersData(...args: unknown[]): unknown;
	refreshUserData(...args: unknown[]): unknown;
	getSourceUsernameById(userId: number | string | undefined): string | undefined;
	banks: any;
	setBanksData(...args: unknown[]): unknown;
	currencies: any;
	setCurrenciesData(...args: unknown[]): unknown;
	currencyPolicies: any;
	setCurrencyPolicyDetails(...args: unknown[]): unknown;
}

const ConfigContext = createContext<IConfigContextProps>({} as IConfigContextProps);

interface IConfigContextProviderProps {
	children: ReactNode;
}

export const ConfigContextProvider: FC<IConfigContextProviderProps> = ({ children }) => {
	const { userData, dealerPermissions } = useContext(AuthContext);
	const { subscribeToToken } = useLiveReflections();
	const [publicSettings, setPublicSettings] = useState<Partial<IPublicSettingsProps>>(
		JSON.parse(localStorage.getItem('ark_public_settings') || '{}'),
	);
	const [users, setUsers] = useState<Partial<IUserProps>[]>([]);
	const [lastUsersFetchedAt, setLastUsersFetchedAt] = useState<string | null>(
		localStorage.getItem('ark_lastUsersFetchedAt'),
	);
	const [currencyPolicies, setCurrencyPolicies] = useState<{
		[key: number]: ICurrencyPolicyProps;
	}>({});
	const [usersHMap, setUsersHMap] = useState<Map<number, Partial<IUserProps>>>(new Map());
	const [banks, setBanks] = useState<Partial<IBanksProps>>(
		JSON.parse(localStorage.getItem('ark_banks') || '[{}]'),
	);
	const [currencies, setCurrencies] = useState<ICurrencyProps[]>([]);
	const initialCurrencyToken = useRef(userData.token);
	const [db, setDb] = useState<any>(null);
	const [hasHydratedDb, setHasHydratedDb] = useState(false);
	const dbRef = useRef<any>(null);
	const visibleUsersRef = useRef<Partial<IUserProps>[]>([]);
	const sourceUsersByIdRef = useRef<Map<number, Partial<IUserProps>>>(new Map());
	const hasCompleteSourceUsersRef = useRef(false);
	const sourceRefreshRequiredRef = useRef(false);
	const lastAppliedVisibilityFingerprintRef = useRef<string>();
	const lastAppliedCacheOwnerRef = useRef<string>();
	const isClientOrOfficeLogin = isClientOrOfficeUserType(Number(userData.userTypeId));
	const userCacheOwner = getUserCacheOwner(userData);

	const visibilityPermissions = useMemo<ClientVisibilityPermissions>(
		() =>
			isClientOrOfficeLogin
				? {
						canSeeClientNames: true,
						canSeeMobileNumbers: true,
				  }
				: {
						canSeeClientNames: dealerPermissions.canSeeClientNames === true,
						canSeeMobileNumbers: dealerPermissions.canSeeMobileNumbers === true,
				  },
		[
			dealerPermissions.canSeeClientNames,
			dealerPermissions.canSeeMobileNumbers,
			isClientOrOfficeLogin,
		],
	);
	const visibilityFingerprint = useMemo(
		() => getUserVisibilityFingerprint(visibilityPermissions, userCacheOwner),
		[userCacheOwner, visibilityPermissions],
	);
	const currentVisibilityFingerprintRef = useRef(visibilityFingerprint);
	currentVisibilityFingerprintRef.current = visibilityFingerprint;
	const currentVisibilityPermissionsRef = useRef(visibilityPermissions);
	currentVisibilityPermissionsRef.current = visibilityPermissions;
	const currentIsClientOrOfficeLoginRef = useRef(isClientOrOfficeLogin);
	currentIsClientOrOfficeLoginRef.current = isClientOrOfficeLogin;
	const currentAuthSessionRef = useRef({
		owner: userCacheOwner,
		token: userData.token,
	});
	currentAuthSessionRef.current = {
		owner: userCacheOwner,
		token: userData.token,
	};

	useEffect(() => {
		// Keep the full currency list out of localStorage and reload it for an existing session.
		try {
			localStorage.removeItem('ark_currencies');
		} catch {
			// Removing the old cache is optional; the API remains the source of truth.
		}
		const token = initialCurrencyToken.current;
		if (!token) return;
		let active = true;
		getCurrencies(process.env.REACT_APP_ARKAPIURL, token)
			.then((result) => {
				if (
					active &&
					currentAuthSessionRef.current.token === token &&
					result?.success &&
					Array.isArray(result.data)
				) {
					setCurrencies(result.data);
				}
			})
			.catch(() => {
				// Currency data can still be loaded through the normal login flow.
			});
		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		localStorage.setItem('ark_public_settings', JSON.stringify(publicSettings));
		if (db && hasHydratedDb) {
			const updatedCurrencyPolicies = Object.values(currencyPolicies);
			if (updatedCurrencyPolicies.length > 0) {
				storeCurrencyPolicies(db, updatedCurrencyPolicies);
			}
		}
		localStorage.setItem('ark_banks', JSON.stringify(banks));
	}, [banks, currencyPolicies, db, publicSettings, hasHydratedDb]);

	const setPublicSettingsData = (data: any) => {
		const publicSettingsData = JSON.stringify(data);
		if (publicSettingsData !== '') {
			setPublicSettings(data);
			localStorage.setItem('ark_public_settings', publicSettingsData);
		} else {
			localStorage.removeItem('ark_public_settings');
		}
	};

	const setBanksData = (data: any) => {
		const BanksData = JSON.stringify(data);
		if (BanksData !== '') {
			setBanks(data);
			localStorage.setItem('ark_banks', BanksData);
		} else {
			localStorage.removeItem('ark_banks');
		}
	};

	const applyVisibleUsersState = useCallback(
		(nextUsers: Partial<IUserProps>[], markFetched: boolean) => {
			visibleUsersRef.current = nextUsers;
			setUsers(nextUsers);
			setUsersHMap(buildUserLookup(nextUsers));
			if (!markFetched) {
				return;
			}
			if (nextUsers.length === 0) {
				setLastUsersFetchedAt(null);
				localStorage.removeItem('ark_lastUsersFetchedAt');
				return;
			}
			const fetchedAt = new Date().toISOString();
			setLastUsersFetchedAt(fetchedAt);
			localStorage.setItem('ark_lastUsersFetchedAt', fetchedAt);
		},
		[],
	);

	const updateUsersState = useCallback(
		async (
			nextUsers: Partial<IUserProps>[],
			fingerprint: string,
			markFetched = true,
		) => {
			applyVisibleUsersState(nextUsers, markFetched);
			const activeDb = dbRef.current;
			if (activeDb) {
				await storeUsers(activeDb, nextUsers as IUserProps[], fingerprint);
			}
		},
		[applyVisibleUsersState],
	);

	const resolveVisibilityPermissions = useCallback(
		(input?: UserVisibilityInput): ClientVisibilityPermissions => {
			if (typeof input === 'object' && input !== null && !(input instanceof Boolean)) {
				return {
					canSeeClientNames: input.canSeeClientNames === true,
					canSeeMobileNumbers: input.canSeeMobileNumbers === true,
				};
			}
			if (input === undefined || input === null) {
				return currentVisibilityPermissionsRef.current;
			}

			const requestedNameVisibility =
				input === true || (input instanceof Boolean && input.valueOf() === true);
			const currentPermissions = currentVisibilityPermissionsRef.current;
			const isCurrentClientOrOfficeLogin = currentIsClientOrOfficeLoginRef.current;
			return {
				canSeeClientNames: isCurrentClientOrOfficeLogin
					? requestedNameVisibility
					: currentPermissions.canSeeClientNames === true,
				canSeeMobileNumbers: isCurrentClientOrOfficeLogin
					? true
					: currentPermissions.canSeeMobileNumbers === true,
			};
		},
		[],
	);

	const replaceSourceUsers = useCallback((nextUsers: Partial<IUserProps>[]) => {
		sourceUsersByIdRef.current = buildUserLookup(nextUsers);
		hasCompleteSourceUsersRef.current = true;
	}, []);

	const getSourceUsernameById = useCallback((userId: number | string | undefined) => {
		const normalizedUserId = Number(userId);
		if (!Number.isFinite(normalizedUserId)) {
			return undefined;
		}
		const sourceUser = sourceUsersByIdRef.current.get(normalizedUserId);
		const username = String(sourceUser?.userName ?? sourceUser?.username ?? '').trim();
		return username && username !== '*' ? username : undefined;
	}, []);

	const setUsersData = useCallback(
		async (
			data: any,
			visibilityInput?: UserVisibilityInput,
			requestSession?: UserCacheScope,
		) => {
			const invocationOwner = requestSession
				? getUserCacheOwner(requestSession)
				: userCacheOwner;
			const currentSession = currentAuthSessionRef.current;
			if (
				currentSession.owner !== invocationOwner ||
				(requestSession?.token !== undefined && currentSession.token !== requestSession.token)
			) {
				return false;
			}
			const sourceUsers = Array.isArray(data) ? data.map(normalizeConfigUser) : [];
			const permissions = resolveVisibilityPermissions(visibilityInput);
			const fingerprint = getUserVisibilityFingerprint(permissions, invocationOwner);
			const sanitizedUsers = sourceUsers.map((user) => maskClientUser(user, permissions));
			replaceSourceUsers(sourceUsers);
			lastAppliedVisibilityFingerprintRef.current = fingerprint;
			lastAppliedCacheOwnerRef.current = invocationOwner;
			try {
				await updateUsersState(sanitizedUsers, fingerprint);
			} catch (error) {
				console.error('Error storing users data');
			}
			return true;
		},
		[replaceSourceUsers, resolveVisibilityPermissions, updateUsersState, userCacheOwner],
	);

	const refreshUsersData = useCallback(
		async (visibilityInput?: UserVisibilityInput) => {
			if (!userData?.token) {
				return undefined;
			}
			const requestOwner = getUserCacheOwner(userData);
			const requestToken = userData.token;
			const result = await getUsersDetailsReportUsers(
				process.env.REACT_APP_ARKAPIURL,
				userData,
			);
			const currentSession = currentAuthSessionRef.current;
			if (
				result?.success &&
				currentSession.owner === requestOwner &&
				currentSession.token === requestToken
			) {
				await setUsersData(result.data, visibilityInput);
			}
			return result;
		},
		[setUsersData, userData],
	);

	const refreshUserData = useCallback(
		async (userId: number | string | undefined, visibilityInput?: UserVisibilityInput) => {
			if (!userId || !userData?.token) {
				return undefined;
			}
			const requestOwner = getUserCacheOwner(userData);
			const requestToken = userData.token;
			const result = await getUsersDetailsReportUsers(
				process.env.REACT_APP_ARKAPIURL,
				userData,
				userId,
			);
			const currentSession = currentAuthSessionRef.current;
			if (
				currentSession.owner !== requestOwner ||
				currentSession.token !== requestToken
			) {
				return result;
			}
			const refreshedUser =
				result?.success && Array.isArray(result.data)
					? normalizeConfigUser(result.data[0])
					: undefined;
			if (!refreshedUser) {
				return result;
			}

			const sourceIds = [Number(refreshedUser.id), Number(refreshedUser.userID)].filter((id) =>
				Number.isFinite(id),
			);
			sourceIds.forEach((id) => sourceUsersByIdRef.current.set(id, refreshedUser));
			const permissions = resolveVisibilityPermissions(visibilityInput);
			const fingerprint = getUserVisibilityFingerprint(permissions, requestOwner);
			const sanitizedUser = maskClientUser(refreshedUser, permissions);
			const currentUsers = visibleUsersRef.current;
			const existingIndex = currentUsers.findIndex(
				(user) =>
					Number(user.id ?? user.userID) === Number(refreshedUser.id ?? refreshedUser.userID),
			);
			const nextUsers =
				existingIndex === -1
					? [...currentUsers, sanitizedUser]
					: currentUsers.map((user, index) =>
							index === existingIndex
								? {
										...user,
										...sanitizedUser,
								  }
								: user,
					  );
			lastAppliedVisibilityFingerprintRef.current = fingerprint;
			lastAppliedCacheOwnerRef.current = requestOwner;
			await updateUsersState(nextUsers, fingerprint);
			return result;
		},
		[resolveVisibilityPermissions, updateUsersState, userData],
	);

	const removeUserData = useCallback(
		async (userId: number | string | undefined) => {
			if (!userId || currentAuthSessionRef.current.owner !== userCacheOwner) {
				return;
			}
			const normalizedUserId = Number(userId);
			const sourceUser = sourceUsersByIdRef.current.get(normalizedUserId);
			if (sourceUser) {
				[Number(sourceUser.id), Number(sourceUser.userID)]
					.filter((id) => Number.isFinite(id))
					.forEach((id) => sourceUsersByIdRef.current.delete(id));
			}
			const nextUsers = visibleUsersRef.current.filter(
				(user) =>
					Number(user.id) !== normalizedUserId && Number(user.userID) !== normalizedUserId,
			);
			lastAppliedVisibilityFingerprintRef.current = visibilityFingerprint;
			lastAppliedCacheOwnerRef.current = userCacheOwner;
			await updateUsersState(nextUsers, visibilityFingerprint);
		},
		[updateUsersState, userCacheOwner, visibilityFingerprint],
	);

	useEffect(() => {
		let isActive = true;
		(async () => {
			try {
				const dbint = await openDatabase();
				if (!isActive) {
					return;
				}
				dbRef.current = dbint;
				setDb(dbint);

				if (hasCompleteSourceUsersRef.current) {
					await storeUsers(
						dbint,
						visibleUsersRef.current as IUserProps[],
						lastAppliedVisibilityFingerprintRef.current ||
							currentVisibilityFingerprintRef.current,
					);
				} else {
					const expectedFingerprint = currentVisibilityFingerprintRef.current;
					const tempUsers = await getAllUsers(dbint, expectedFingerprint);
					// Login or a reflection can populate a newer source collection while the
					// cache read is in flight. Never let that older cache overwrite it.
					if (hasCompleteSourceUsersRef.current) {
						await storeUsers(
							dbint,
							visibleUsersRef.current as IUserProps[],
							lastAppliedVisibilityFingerprintRef.current || expectedFingerprint,
						);
					} else if (tempUsers === null) {
						await removeAllUsers(dbint);
						applyVisibleUsersState([], true);
						sourceRefreshRequiredRef.current = true;
					} else {
						const normalizedUsers = tempUsers.map(normalizeConfigUser);
						applyVisibleUsersState(normalizedUsers, false);
						// Persisted records intentionally contain no raw identifiers. Re-fetch them
						// before an action can resolve a source username.
						sourceRefreshRequiredRef.current = normalizedUsers.length > 0;
					}
					if (!hasCompleteSourceUsersRef.current) {
						lastAppliedVisibilityFingerprintRef.current = expectedFingerprint;
						lastAppliedCacheOwnerRef.current = currentAuthSessionRef.current.owner;
					}
				}

				const tempCurrencyPolicies = await getAllCurrencyPolicies(dbint);
				if (tempCurrencyPolicies.length > 0) {
					const currencyPolicyMap: { [key: number]: ICurrencyPolicyProps } = {};
					for (const currencyPolicy of tempCurrencyPolicies) {
						currencyPolicyMap[currencyPolicy?.currencyId || -1] = currencyPolicy;
					}
					setCurrencyPolicies(currencyPolicyMap);
				}
			} catch (error) {
				console.error('Error initializing database');
			} finally {
				if (isActive) {
					setHasHydratedDb(true);
				}
			}
		})();

		return () => {
			isActive = false;
		};
	}, [applyVisibleUsersState]);

	useEffect(() => {
		if (!hasHydratedDb || !sourceRefreshRequiredRef.current || !userData?.token) {
			return;
		}
		sourceRefreshRequiredRef.current = false;
		refreshUsersData();
	}, [hasHydratedDb, refreshUsersData, userData?.token]);

	useLayoutEffect(() => {
		if (!hasHydratedDb) {
			return;
		}
		const previousFingerprint = lastAppliedVisibilityFingerprintRef.current;
		const previousCacheOwner = lastAppliedCacheOwnerRef.current;
		if (!previousFingerprint || previousFingerprint === visibilityFingerprint) {
			lastAppliedVisibilityFingerprintRef.current = visibilityFingerprint;
			lastAppliedCacheOwnerRef.current = userCacheOwner;
			return;
		}

		lastAppliedVisibilityFingerprintRef.current = visibilityFingerprint;
		lastAppliedCacheOwnerRef.current = userCacheOwner;
		if (previousCacheOwner && previousCacheOwner !== userCacheOwner) {
			sourceUsersByIdRef.current = new Map();
			hasCompleteSourceUsersRef.current = false;
			updateUsersState([], visibilityFingerprint);
			if (userData?.token) {
				refreshUsersData();
			} else {
				sourceRefreshRequiredRef.current = true;
			}
			return;
		}
		if (hasCompleteSourceUsersRef.current) {
			const sourceUsers = Array.from(new Set(sourceUsersByIdRef.current.values()));
			const sanitizedUsers = sourceUsers.map((user) =>
				maskClientUser(user, visibilityPermissions),
			);
			updateUsersState(sanitizedUsers, visibilityFingerprint);
			return;
		}

		// Cached values were stored for another permission state and have no raw source.
		// Remove them immediately; a fresh response will repopulate a correctly masked cache.
		updateUsersState([], visibilityFingerprint);
		if (userData?.token) {
			refreshUsersData();
		} else {
			sourceRefreshRequiredRef.current = true;
		}
	}, [
		hasHydratedDb,
		refreshUsersData,
		updateUsersState,
		userData?.token,
		userCacheOwner,
		visibilityFingerprint,
		visibilityPermissions,
	]);

	useEffect(() => {
		const userRefreshTokens = [
			LiveReflectionToken.CreateClientOffice,
			LiveReflectionToken.CreateDealer,
			LiveReflectionToken.UpdateClientOffice,
			LiveReflectionToken.UpdateDealer,
		];
		const fullRefreshTokens = [
			LiveReflectionToken.TransferClientOffice,
			LiveReflectionToken.TransferDealer,
		];
		const deleteTokens = [
			LiveReflectionToken.DeleteClientOffice,
			LiveReflectionToken.DeleteDealer,
			LiveReflectionToken.HardDeleteUserCompleted,
		];

		const getAffectedUserId = (event: LiveReflectionEvent) =>
			event.affectedId ||
			Number((event.data as any)?.userId || (event.data as any)?.userID || (event.payload as any)?.USER_ID);

		const unsubscribeUserRefresh = subscribeToToken(userRefreshTokens, (event) => {
			const affectedUserId = getAffectedUserId(event);
			if (affectedUserId) {
				refreshUserData(affectedUserId);
			}
		});

		const unsubscribeFullRefresh = subscribeToToken(fullRefreshTokens, () => {
			refreshUsersData();
		});

		const unsubscribeDeletes = subscribeToToken(deleteTokens, (event) => {
			const affectedUserId = getAffectedUserId(event);
			if (affectedUserId) {
				removeUserData(affectedUserId);
			} else {
				refreshUsersData();
			}
		});

		return () => {
			unsubscribeUserRefresh();
			unsubscribeFullRefresh();
			unsubscribeDeletes();
		};
	}, [refreshUserData, refreshUsersData, removeUserData, subscribeToToken]);

	const setCurrencyPolicyDetails = useCallback(
		async (data: any) => {
			try {
				const currencyPolicyMap: { [key: number]: ICurrencyPolicyProps } = {};
				if (data && data.length > 0) {
					for (const currencyPolicy of data) {
						currencyPolicyMap[currencyPolicy.currencyId] = currencyPolicy;
					}
					setCurrencyPolicies((prev) => ({ ...prev, ...currencyPolicyMap })); // Preserve previous policies
					await storeCurrencyPolicies(db, data);
				} else {
					await removeCurrencyPolicies(db);
					setCurrencyPolicies({});
				}
			} catch (e) {
				console.error('Error while setting currency policies');
			}
		},
		[db],
	);

	const setCurrenciesData = (data: any) => {
		setCurrencies(Array.isArray(data) ? data : []);
	};

	const value = useMemo(
		() => ({
			usersHMap,
			publicSettings,
			setPublicSettingsData,
			users,
			lastUsersFetchedAt,
			setUsersData,
			refreshUsersData,
			refreshUserData,
			getSourceUsernameById,
			banks,
			setBanksData,
			currencies,
			setCurrenciesData,
			currencyPolicies,
			setCurrencyPolicyDetails,
		}),
		[
			usersHMap,
			users,
			lastUsersFetchedAt,
			banks,
			currencies,
			publicSettings,
			setUsersData,
			refreshUsersData,
			refreshUserData,
			getSourceUsernameById,
			currencyPolicies,
			setCurrencyPolicyDetails,
		],
	);

	return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};

ConfigContextProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export default ConfigContext;
